import axios from 'axios';
import qs from 'qs';
import { getApiV3Url, getApiV4Url } from '../utils/api';
import {
  Property, PropertyAggregations, PropertySearchResult,
} from '../dtos/property';
import { serializeParams, serializeRadius } from '../utils/serializer';
import { PaginationParams } from '../../domain/pagination';
import { serializePagination } from '../pagination';
import { PropertiesResponse, PropertyFilters } from './types';

export * from './types';

export const BASE_PROPERTIES_URL = `${getApiV3Url()}/properties`;
export const BASE_PROPERTIES_URL_V4 = `${getApiV4Url()}/properties`;

export async function getAll(
  filters: PropertyFilters = {},
  pagination: PaginationParams = { size: 15 },
): Promise<PropertiesResponse> {
  const passedFilters: PropertyFilters = {
    ...filters,
    area: filters.locality && filters.area ? filters.area : undefined,
  };

  const params = {
    ...serializeParams(passedFilters),
    ...serializeRadius(passedFilters),
    ...serializePagination(pagination),
  };

  const { data } = await axios.get<PropertiesResponse>(BASE_PROPERTIES_URL_V4, {
    params,
    paramsSerializer: (p: any): string => qs.stringify(p, { arrayFormat: 'repeat' }),
  });

  return data;
}

export async function getById(id: number): Promise<Property> {
  return axios
    .get<Property>(`${BASE_PROPERTIES_URL}/${id}`)
    .then(({ data }) => data)
    .catch((error) => {
      if (error.response.status === 404) {
        return undefined;
      }
      throw error;
    });
}

export async function getByIdV4(id: number): Promise<Property> {
  return axios
    .get<Property>(`${BASE_PROPERTIES_URL_V4}/${id}`)
    .then(({ data }) => data)
    .catch((error) => {
      if (error.response.status === 404) {
        return undefined;
      }
      throw error;
    });
}

export async function getAggregationsByProvinceAndLocality(soldAt?: string, minCount: number = 2): Promise<PropertyAggregations> {
  const { data } = await axios.get(`${BASE_PROPERTIES_URL_V4}/aggregations/geographies`, {
    params: {
      start_geography: 'province',
      depth: 2,
      min_count: minCount,
      sold_at_min: soldAt,
    },
  });

  return data;
}

export async function getAggregationsByProvince(province: string, soldAt?: string, minCount: number = 2): Promise<PropertyAggregations> {
  const { data } = await axios.get(`${BASE_PROPERTIES_URL_V4}/aggregations/geographies`, {
    params: {
      start_geography: 'province',
      depth: 2,
      min_count: minCount,
      geography_filter: `province:${province}`,
      sold_at_min: soldAt,
    },
  });

  return data;
}

export async function getAggregationsByArea(locality: string, soldAt?: string, minCount: number = 2): Promise<PropertyAggregations> {
  const { data } = await axios.get(`${BASE_PROPERTIES_URL_V4}/aggregations/geographies`, {
    params: {
      start_geography: 'area',
      min_count: minCount,
      geography_filter: `locality:${locality}`,
      sold_at_min: soldAt,
    },
  });

  return data;
}

export async function getLatestSold(
  filters: Pick<PropertyFilters, 'province'>,
  pagination: Pick<PaginationParams, 'size'>,
): Promise<PropertySearchResult[]> {
  const { data } = await axios.get<PropertySearchResult[]>(`${BASE_PROPERTIES_URL_V4}/latest-sold`, {
    params: {
      ...serializeParams(filters),
      ...serializePagination(pagination),
    },
  });

  return data;
}

export async function getSimilarTo(id: number): Promise<PropertySearchResult[]> {
  const { data } = await axios.get<PropertySearchResult[]>(`${BASE_PROPERTIES_URL_V4}/${id}/similar-to`);

  return data;
}
